import { useCallback, useState } from 'react';
import { useService } from '../../../contexts/ServiceContext';
import {
  SearchListingsRequest,
  ListingSummaryResponse,
  ListingDetailResponse,
  ListingData,
  PaginationMetadata,
} from '../types';

/**
 * Options for the listing search hook
 */
interface UseListingSearchOptions {
  /** Called when an error occurs */
  onError?: (error: Error) => void;
}

/**
 * Return type for the useListingSearch hook
 */
interface UseListingSearchReturn {
  /** Current listings (includes both skeleton and full data) */
  listings: ListingData[];
  /** Pagination metadata */
  pagination: PaginationMetadata | null;
  /** Whether the initial summary is loading */
  isLoadingSummary: boolean;
  /** Whether we're loading a new page */
  isLoadingPage: boolean;
  /** Number of details loaded */
  loadedDetailsCount: number;
  /** Total expected details (from summary) */
  totalExpectedDetails: number;
  /** Error message if any */
  error: string | null;
  /** Function to execute a search */
  executeSearch: (request: SearchListingsRequest) => Promise<void>;
  /** Clear current results */
  clear: () => void;
}

/**
 * Hook for executing listing searches via WebSocket with progressive loading.
 *
 * This hook handles the multi-response pattern:
 * 1. Summary response - creates skeleton cards
 * 2. Detail responses - progressively updates each card
 * 3. Complete response - all data loaded
 *
 * @example
 * ```typescript
 * const { listings, pagination, executeSearch, isLoadingSummary } = useListingSearch();
 *
 * useEffect(() => {
 *   executeSearch({
 *     page: 1,
 *     perPage: 20,
 *     filters: { mlsStatus: 'Active' }
 *   });
 * }, []);
 * ```
 */
export function useListingSearch(
  options: UseListingSearchOptions = {},
): UseListingSearchReturn {
  const { onError } = options;
  const { searchListingsPaginated, isWebSocketReady } = useService();

  const [listings, setListings] = useState<ListingData[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [loadedDetailsCount, setLoadedDetailsCount] = useState(0);
  const [totalExpectedDetails, setTotalExpectedDetails] = useState(0);
  const [error, setError] = useState<string | null>(null);

  /**
   * Execute a listing search
   */
  const executeSearch = useCallback(
    async (request: SearchListingsRequest) => {
      if (!isWebSocketReady()) {
        const err = new Error('WebSocket is not connected');
        setError(err.message);
        if (onError) onError(err);
        return;
      }

      try {
        // Reset state for new search
        setIsLoadingSummary(true);
        setIsLoadingPage(true);
        setError(null);
        setLoadedDetailsCount(0);
        setTotalExpectedDetails(0);

        await searchListingsPaginated(
          request,
          // onSummary
          (response: ListingSummaryResponse) => {
            // Summary received - create skeleton cards
            const skeletonListings = response.summary.listings.map(
              (listing) => ({
                ...listing,
                IsSummary: true, // Mark as skeleton
              }),
            );

            setListings(skeletonListings);
            setPagination(response.summary.pagination);
            setTotalExpectedDetails(skeletonListings.length);
            setIsLoadingSummary(false);
          },
          // onDetail
          (response: ListingDetailResponse) => {
            // Detail received - update specific listing
            setListings((prev) =>
              prev.map((listing) =>
                listing.Property.LISTING_KEY ===
                response.listing.Property.LISTING_KEY
                  ? {
                      ...response.listing,
                      IsSummary: false, // Mark as full data
                    }
                  : listing,
              ),
            );

            setLoadedDetailsCount((prev) => prev + 1);
          },
          // onComplete
          () => {
            // All details received
            setIsLoadingPage(false);
          },
          // onError
          (err: Error) => {
            setError(err.message);
            setIsLoadingSummary(false);
            setIsLoadingPage(false);
            if (onError) onError(err);
            console.error('Listing search error:', err);
          },
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to search listings';
        setError(errorMessage);
        setIsLoadingSummary(false);
        setIsLoadingPage(false);
        if (onError && err instanceof Error) onError(err);
        console.error('Listing search error:', err);
      }
    },
    [searchListingsPaginated, isWebSocketReady, onError],
  );

  /**
   * Clear current results
   */
  const clear = useCallback(() => {
    setListings([]);
    setPagination(null);
    setIsLoadingSummary(false);
    setIsLoadingPage(false);
    setLoadedDetailsCount(0);
    setTotalExpectedDetails(0);
    setError(null);
  }, []);

  return {
    listings,
    pagination,
    isLoadingSummary,
    isLoadingPage,
    loadedDetailsCount,
    totalExpectedDetails,
    error,
    executeSearch,
    clear,
  };
}
